import { api } from '../lib/api';
import type { VideoSettings } from '../lib/types';

/**
 * Recording defaults: the settings the share page gives a recording's owner,
 * starting with who may watch, answered once instead of per recording.
 *
 * A team can answer them for everyone in it by locking one, which is the only
 * reason this is more than a form over a single object.
 */

export const VIDEO_SETTING_KEYS = [
  'visibility',
  'captionsOn',
  'playbackRate',
  'emailGate',
  'commentsEnabled',
  'reactionsEnabled',
  'downloadEnabled',
  'analyticsPublic',
  'transcriptPublic',
  'linksAtEnd',
] as const;

export type VideoSettingKey = (typeof VIDEO_SETTING_KEYS)[number];

export interface VideoDefaults {
  /** What this person has chosen. Anything absent falls through. */
  personal: Partial<VideoSettings>;
  /** What their team has chosen, if they are in one. */
  org: Partial<VideoSettings>;
  /** The settings their team answers for everyone. */
  locked: VideoSettingKey[];
  /** What their next recording will actually start with. */
  effective: VideoSettings;
  team: { id: string; name: string | null; canManage: boolean } | null;
}

export function fetchVideoDefaults(): Promise<VideoDefaults> {
  return api<VideoDefaults>('/v1/me/video-defaults');
}

/** A partial: null on a setting hands it back to the team or the system. */
export function setMyVideoDefaults(
  patch: Partial<Record<VideoSettingKey, unknown>>,
): Promise<{ personal: Partial<VideoSettings>; effective: VideoSettings }> {
  return api<{ personal: Partial<VideoSettings>; effective: VideoSettings }>('/v1/me/video-defaults', {
    method: 'PUT',
    body: patch,
  });
}

/** The team's own defaults, and which of them it insists on. Admins and owners. */
export function setTeamVideoDefaults(
  orgId: string,
  body: { defaults?: Partial<Record<VideoSettingKey, unknown>>; locked?: VideoSettingKey[] },
): Promise<{ defaults: Partial<VideoSettings>; locked: VideoSettingKey[] }> {
  return api<{ defaults: Partial<VideoSettings>; locked: VideoSettingKey[] }>(`/v1/orgs/${orgId}/video-defaults`, {
    method: 'PUT',
    body,
  });
}
